import React, {forwardRef, useCallback, useEffect, useRef, useState} from "react"
import styles from "./Input.module.scss"
import classNames from "classnames"
import {v4 as uuid} from "uuid"
import {InputProps} from "./Interfaces"
import {InputWrapper} from "./InputWrapper"
import {MaskInput} from "./MaskInputLazy"

export const Input = forwardRef<HTMLInputElement, InputProps>((params, ref) => {
    const {
        className,
        label,
        noTopPadding,
        loader,
        error,
        warning,
        noErrorIfActive,
        active,
        style,
        type,
        value,
        mask,
        onChange,
        fullWidth,
        align,
        miniSize,
        maxLength,
        inListNumber,
        inputClassName,
        withoutUnderline,
        conditionsIsVisible,
        validationDataTest,
        underlineChildren,
        errorUnderline,
        hideErrorText,
        ...props
    } = params

    const inputRef = useRef<HTMLInputElement | any>(null)

    const [id] = useState(() => `input_${uuid().substr(0, 10)}`)
    const labelCover = !!(params.label && !value && !params.placeholder)

    useEffect(() => {
        const handler = (e) => {
            if (!e.key) return

            const invalidChars = ["-", "+", "e", ",", "."]
            if (invalidChars.includes(e.key)) {
                e.preventDefault()
                return
            }
        }

        if (inputRef.current && type === "number") {
            inputRef.current.addEventListener("keydown", handler)
        }

        return () => {
            if (inputRef.current) inputRef.current.removeEventListener("keydown", handler)
        }
    }, [type, props.max, maxLength])

    const handleAccept = useCallback(
        (unmaskValue: React.ChangeEvent<HTMLInputElement>, mask: any) => {
            if (!unmaskValue) {
                if (mask?.el?.value) mask.el.value = ""
                mask.updateValue()
            }

            if (onChange) {
                onChange(unmaskValue)
            }
        },
        [onChange]
    )

    return (
        <InputWrapper
            withoutUnderline={withoutUnderline}
            className={className}
            label={label}
            noTopPadding={noTopPadding}
            labelHtmlFor={id}
            loader={loader}
            error={noErrorIfActive ? (active ? undefined : error) : error}
            validationDataTest={validationDataTest}
            warning={warning}
            style={style}
            type={type}
            fullWidth={fullWidth}
            labelCover={labelCover}
            conditionsIsVisible={conditionsIsVisible}
            password={type === "password"}
            align={align}
            miniSize={miniSize}
            name={props.name}
            value={value}
            underlineChildren={underlineChildren}
            errorUnderline={errorUnderline}
            hideErrorText={hideErrorText}
        >
            {mask ? (
                <MaskInput
                    mask={mask}
                    unmask
                    lazy={!value?.length}
                    onAccept={handleAccept}
                    id={id}
                    className={!value?.length ? styles.noValue : undefined}
                    ref={ref ? ref : inputRef}
                    type={type}
                    value={value}
                    {...props}
                    onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
                        event.preventDefault()
                        props.onBlur && props.onBlur(event)
                    }}
                />
            ) : (
                <input
                    className={classNames(inputClassName, {
                        [styles["-inListNumber"]]: inListNumber,
                        [styles.noValue]: !value?.length,
                    })}
                    maxLength={maxLength}
                    id={id}
                    ref={ref ? ref : inputRef}
                    type={type}
                    value={value}
                    onChange={onChange}
                    {...props}
                    capture={undefined}
                />
            )}
        </InputWrapper>
    )
})
